import textMaskCreateNumberMask from 'text-mask-addons/dist/createNumberMask';

// Custom createNumberMask implementation for better cursor control
export function createNumberMask(config = {}) {
  return {
    ...config,
    // This is used by our masking system to identify it as a number mask
    __isNumberMask: true
  };
}

// Vue 3 compatible input mask directive
export const vMask = {
  beforeMount(el, binding) {
    applyMask(el, binding.value);
  },
  updated(el, binding) {
    if (binding.value !== binding.oldValue) {
      applyMask(el, binding.value);
    }
  }
};

function applyMask(el, maskValue) {
  const input = el.tagName === 'INPUT' ? el : el.querySelector('input');
  if (!input) return;

  if (input._maskHandler) {
    input.removeEventListener('input', input._maskHandler);
    input.removeEventListener('blur', input._maskBlurHandler);
    input.removeEventListener('keydown', input._maskKeydownHandler);
  }

  if (!maskValue) return;

  // Create mask handlers
  input._maskHandler = (e) => {
    const cursorPosition = input.selectionStart;
    const maskedValue = applyMaskToValue(e.target.value, maskValue);
    
    if (maskedValue !== e.target.value) {
      e.target.value = maskedValue;
      
      // For masks with suffix (like %), position cursor before the suffix
      if (typeof maskValue === 'object' && maskValue.suffix) {
        const suffixLength = maskValue.suffix.length;
        const newCursorPosition = Math.max(0, maskedValue.length - suffixLength);
        setTimeout(() => {
          input.setSelectionRange(newCursorPosition, newCursorPosition);
        }, 0);
      } else if (typeof maskValue === 'string') {
        // Restore cursor position for string masks
        const newCursorPosition = Math.min(cursorPosition, maskedValue.length);
        setTimeout(() => {
          input.setSelectionRange(newCursorPosition, newCursorPosition);
        }, 0);
      }
      
      // Trigger input event for Vue reactivity
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  // Blur handler for number masks to ensure proper formatting
  input._maskBlurHandler = (e) => {
    if (typeof maskValue === 'object') {
      const maskedValue = applyMaskToValue(e.target.value, maskValue);
      if (maskedValue !== e.target.value) {
        e.target.value = maskedValue;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  };

  // Add keydown handler for better backspace behavior with suffix
  input._maskKeydownHandler = (e) => {
    if (typeof maskValue === 'object' && maskValue.suffix && e.key === 'Backspace') {
      const cursorPosition = input.selectionStart;
      const suffixLength = maskValue.suffix.length;
      const currentValue = input.value;
      const valueWithoutSuffix = currentValue.substring(0, currentValue.length - suffixLength);
      
      // If cursor is at or after the suffix position
      if (cursorPosition >= valueWithoutSuffix.length) {
        e.preventDefault();
        
        // Remove the last character from the number part
        if (valueWithoutSuffix.length > 0) {
          const newValueWithoutSuffix = valueWithoutSuffix.substring(0, valueWithoutSuffix.length - 1);
          const newValue = applyMaskToValue(newValueWithoutSuffix, maskValue);
          input.value = newValue;
          
          // Position cursor at the end of the number part
          const newPosition = Math.max(0, newValue.length - suffixLength);
          setTimeout(() => {
            input.setSelectionRange(newPosition, newPosition);
          }, 0);
          
          // Trigger input event for Vue reactivity
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    }
  };

  input.addEventListener('input', input._maskHandler);
  input.addEventListener('blur', input._maskBlurHandler);
  input.addEventListener('keydown', input._maskKeydownHandler);
  
  // Apply initial mask
  if (input.value) {
    input.value = applyMaskToValue(input.value, maskValue);
  }
}

function applyMaskToValue(value, maskValue) {
  if (!value) return value;
  
  if (typeof maskValue === 'string') {
    // Simple string mask (like phone numbers: "(###) ###-####")
    return applyStringMask(value, maskValue);
  } else if (typeof maskValue === 'object' && (maskValue.__isNumberMask || maskValue.prefix !== undefined || maskValue.suffix !== undefined)) {
    // Number mask from our custom createNumberMask or text-mask-addons
    return applyNumberMask(value, maskValue);
  } else if (typeof maskValue === 'function') {
    // Function mask from text-mask-addons createNumberMask
    return applyNumberMask(value, maskValue.instanceOptions || {});
  }
  return value;
}

function applyStringMask(value, mask) {
  // Remove all non-alphanumeric characters for cleaning
  const cleanValue = value.replace(/[^\w]/g, '');
  let maskedValue = '';
  let valueIndex = 0;
  
  for (let i = 0; i < mask.length && valueIndex < cleanValue.length; i++) {
    if (mask[i] === '#') {
      maskedValue += cleanValue[valueIndex];
      valueIndex++;
    } else {
      maskedValue += mask[i];
    }
  }
  
  return maskedValue;
}

function applyNumberMask(value, maskConfig) {
  if (!value || value === '') return '';
  
  // Clean the value - remove everything except digits and decimal point
  let cleanValue = value.replace(/[^\d.]/g, '');
  
  // Handle multiple decimal points - keep only the first one
  const decimalIndex = cleanValue.indexOf('.');
  if (decimalIndex !== -1) {
    cleanValue = cleanValue.substring(0, decimalIndex + 1) + cleanValue.substring(decimalIndex + 1).replace(/\./g, '');
  }
  
  // Handle negative values
  if (!maskConfig.allowNegative && cleanValue.startsWith('-')) {
    cleanValue = cleanValue.substring(1);
  }
  
  // Apply integer limit before parsing - this prevents entering too many digits
  if (maskConfig.integerLimit && cleanValue.includes('.')) {
    const parts = cleanValue.split('.');
    if (parts[0].length > maskConfig.integerLimit) {
      parts[0] = parts[0].substring(0, maskConfig.integerLimit);
      cleanValue = parts.join('.');
    }
  } else if (maskConfig.integerLimit && cleanValue.length > maskConfig.integerLimit && !cleanValue.includes('.')) {
    cleanValue = cleanValue.substring(0, maskConfig.integerLimit);
  }
  
  // If it's just a decimal point, return empty or handle specially
  if (cleanValue === '.') {
    return maskConfig.prefix || '';
  }
  
  // Parse as number
  let numericValue = parseFloat(cleanValue);
  if (isNaN(numericValue)) {
    // If we have a partial decimal input like "4.", handle it
    if (cleanValue.endsWith('.') && cleanValue.length > 1) {
      numericValue = parseFloat(cleanValue.substring(0, cleanValue.length - 1));
      if (!isNaN(numericValue)) {
        let result = (maskConfig.prefix || '') + numericValue.toString();
        if (maskConfig.allowDecimal !== false) {
          result += (maskConfig.decimalSymbol || '.');
        }
        result += (maskConfig.suffix || '');
        return result;
      }
    }
    return (maskConfig.prefix || '') + (maskConfig.suffix || '');
  }
  
  // Apply negative constraint
  if (!maskConfig.allowNegative && numericValue < 0) {
    numericValue = Math.abs(numericValue);
  }
  
  // Format the number
  let formattedValue = '';
  
  // Add prefix
  if (maskConfig.prefix) {
    formattedValue += maskConfig.prefix;
  }
  
  // Handle decimal formatting
  let numberStr;
  if (maskConfig.allowDecimal !== false && maskConfig.decimalLimit !== undefined) {
    // Don't automatically add trailing zeros for intermediate input
    if (cleanValue.includes('.')) {
      const decimalPart = cleanValue.split('.')[1] || '';
      const limitedDecimalPart = decimalPart.substring(0, maskConfig.decimalLimit);
      const integerPart = Math.floor(numericValue).toString();
      numberStr = integerPart + (limitedDecimalPart.length > 0 ? '.' + limitedDecimalPart : (cleanValue.endsWith('.') ? '.' : ''));
    } else {
      numberStr = Math.floor(numericValue).toString();
    }
  } else if (maskConfig.allowDecimal === false) {
    numberStr = Math.floor(numericValue).toString();
  } else {
    numberStr = numericValue.toString();
  }
  
  // Add thousands separator if enabled
  if (maskConfig.includeThousandsSeparator) {
    const parts = numberStr.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, maskConfig.thousandsSeparatorSymbol || ',');
    numberStr = parts.join(maskConfig.decimalSymbol || '.');
  }
  
  formattedValue += numberStr;
  
  // Add suffix
  if (maskConfig.suffix) {
    formattedValue += maskConfig.suffix;
  }
  
  return formattedValue;
}

export const currencyMask = createNumberMask({
  prefix: '$',
  suffix: '',
  includeThousandsSeparator: true,
  allowNegative: false,
  thousandsSeparatorSymbol: ',',
  allowDecimal: true,
  decimalSymbol: '.',
  decimalLimit: 2
});

export const percentageMask = createNumberMask({
  prefix: '',
  suffix: '%',
  allowDecimal: true,
  decimalLimit: 2,
  integerLimit: 2,
  allowNegative: false,
  includeThousandsSeparator: false,
  decimalSymbol: '.'
});

